import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, ArrowRight, Sun, DoorOpen, HeartHandshake, Users, Eye } from "lucide-react";
import { CHURCH, IMAGES } from "@/data/church";
import { Reveal } from "@/components/Reveal";
import { GoldDivider } from "@/components/GoldDivider";
import { Marquee } from "@/components/Marquee";

const HERO_EASE = [0.22, 1, 0.36, 1];
const MASK_INITIAL = { y: "115%" };
const MASK_ANIMATE = { y: 0 };
const BADGE_ANIM = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay: 0.65, ease: HERO_EASE },
};
const CTA_ANIM = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay: 0.8, ease: HERO_EASE },
};

const MaskedLine = ({ children, delay = 0 }) => {
  const transition = useMemo(() => ({ duration: 0.95, delay, ease: HERO_EASE }), [delay]);
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={MASK_INITIAL}
        animate={MASK_ANIMATE}
        transition={transition}
      >
        {children}
      </motion.span>
    </span>
  );
};

const weeklyVisitors = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const week = Math.floor((now - start) / 604800000);
  const seed = (now.getFullYear() * 100 + week) * 2654435761;
  return 15 + (Math.abs(seed) % 31);
};

const Hero = () => (
  <section
    data-testid="home-hero"
    className="spiritual-pattern relative flex min-h-[92vh] items-center overflow-hidden"
  >
    <div className="relative mx-auto w-full max-w-5xl px-5 py-24 text-center sm:px-8">
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.12] text-[#101826]">
        Stafford Spiritualist
        <span className="block">Church</span>
      </h1>

      <div className="mt-5 text-2xl sm:text-3xl font-serif italic">
        <MaskedLine delay={0.38}>
          <span className="inline-block rounded-full bg-[#6B21A8] px-8 py-2.5 text-[#FBF6EC] shadow-lg">
            Everyone is Welcome
          </span>
        </MaskedLine>
      </div>

      <motion.div
        {...BADGE_ANIM}
        className="mx-auto mt-10 inline-flex flex-col items-center gap-1 rounded-2xl border-2 border-[#C59B4B] bg-white/85 px-8 py-5 shadow-[0_12px_40px_rgba(88,28,135,0.15)] backdrop-blur-sm"
        data-testid="hero-service-badge"
      >
        <span className="flex items-center gap-2 text-base sm:text-lg font-semibold uppercase tracking-wide text-[#6B21A8]">
          <Sun className="h-5 w-5" aria-hidden="true" />
          Sunday Divine Service
        </span>
        <span className="font-serif text-2xl sm:text-3xl font-semibold text-[#101826]">
          Every Sunday at 6:30pm
        </span>
      </motion.div>

      <motion.div
        {...CTA_ANIM}
        className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
      >
        <Link
          to="/contact"
          data-testid="hero-plan-visit-button"
          className="inline-flex min-h-14 items-center gap-3 rounded-xl bg-[#6B21A8] px-8 text-lg font-semibold text-[#FBF6EC] shadow-md transition-all duration-200 hover:bg-[#7C2DAE] hover:shadow-lg active:scale-95"
        >
          Plan Your First Visit
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Link>
        <a
          href={CHURCH.phoneHref}
          data-testid="hero-call-button"
          className="inline-flex min-h-14 items-center gap-3 rounded-xl border-2 border-[#C59B4B] bg-[#C59B4B] px-8 text-lg font-semibold text-[#101826] shadow-md transition-all duration-200 hover:bg-[#d4ab5f] hover:border-[#d4ab5f] hover:shadow-lg active:scale-95"
        >
          <Phone className="h-5 w-5" aria-hidden="true" />
          Call {CHURCH.phoneDisplay}
        </a>
      </motion.div>

      <motion.p
        {...CTA_ANIM}
        data-testid="weekly-visitors"
        className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-[#C59B4B] bg-white/85 px-6 py-2.5 text-base font-semibold text-[#6B21A8] shadow-md backdrop-blur-sm"
      >
        <Eye className="h-5 w-5 text-[#C59B4B]" aria-hidden="true" />
        {weeklyVisitors()} visitors this week
      </motion.p>
    </div>
  </section>
);

const ServiceSpotlight = () => (
  <section id="service" data-testid="service-section" className="relative bg-gradient-to-b from-[#F7ECD9] to-[#FBF6EC] py-20 sm:py-28">
    <div className="mx-auto max-w-4xl px-5 sm:px-8">
      <Reveal>
        <div className="rounded-3xl border-2 border-[#C59B4B]/70 bg-gradient-to-br from-[#6B21A8] to-[#A61C4E] p-8 text-center shadow-[0_16px_50px_rgba(88,28,135,0.28)] sm:p-14">
          <p className="text-sm sm:text-base font-semibold uppercase tracking-[0.25em] text-[#F2C14E]">
            Join Us This Week
          </p>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#FBF6EC]">
            Sunday Divine Service
          </h2>
          <p className="mt-6 font-serif text-5xl sm:text-6xl font-semibold text-[#F2C14E]">
            6:30<span className="text-3xl sm:text-4xl">pm</span>
          </p>
          <p className="mt-2 text-lg sm:text-xl text-[#F3E8FA]">Every Sunday Evening</p>

          <GoldDivider className="mx-auto my-8 max-w-xs" />

          <p className="flex items-center justify-center gap-2 text-base sm:text-lg font-medium text-[#F3E8FA]">
            <HeartHandshake className="h-5 w-5 text-[#F2C14E]" aria-hidden="true" />
            Healing is available before all Sunday services
          </p>

          <p className="mx-auto mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#F3E8FA]">
            Come as you are. There is no dress code, no pressure and no expectation —
            just a warm welcome, an uplifting address and a demonstration of mediumship.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const About = () => (
  <section data-testid="about-section" className="bg-[#0F766E] py-20 sm:py-28">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
      <Reveal>
        <div className="relative mx-auto max-w-md">
          <div
            className="absolute -left-4 -top-4 h-full w-full rounded-3xl border-2 border-[#C59B4B]/70"
            aria-hidden="true"
          />
          <img
            src={IMAGES.about.url}
            alt={IMAGES.about.alt}
            loading="lazy"
            className="relative w-full rounded-3xl object-cover shadow-xl"
          />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <p className="text-sm sm:text-base font-semibold uppercase tracking-[0.25em] text-[#F2C14E]">
            About Our Church
          </p>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#FBF6EC]">
            A Peaceful Spiritual Home in the Heart of Stafford
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#E9F2EC]">
            Stafford Spiritualist Church has long been a place of quiet comfort, healing
            and friendship. We gather at the Northfield Centre on Co-operative Street —
            a warm, welcoming hall just a short walk from the town centre.
          </p>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#E9F2EC]">
            Spiritualism affirms that life continues beyond this world, and that those
            we love are never lost to us.
          </p>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#E9F2EC]">
            Whatever you believe, whatever brings you through our doors — curiosity,
            grief, hope or simply a wish for a quiet hour — you are very welcome here.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <GoldDivider className="my-8 max-w-xs" />
          <p className="font-serif text-xl italic text-[#F7D9A0]">
            “Love, like life, can never die.”
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const EXPECT_STEPS = [
  {
    icon: DoorOpen,
    title: "Arrive & Say Hello",
    text: "Come along a little before 6:30pm. Healing is available before the service. Take a seat wherever you feel comfortable.",
  },
  {
    icon: Sun,
    title: "The Service Begins",
    text: "At 6:30pm we begin with a quiet opening prayer and song, followed by a short, thoughtful address about spiritual life.",
  },
  {
    icon: HeartHandshake,
    title: "Messages & Mediumship",
    text: "Our mediums will bring you messages from your loved ones.",
  },
  {
    icon: Users,
    title: "Stay & Chat",
    text: "When the service ends there is no rush to leave. If you have questions about the church or mediumship, this is the perfect moment to ask.",
  },
];

const STEP_COLORS = ["bg-[#6B21A8]", "bg-[#0F766E]", "bg-[#A61C4E]", "bg-[#047857]"];

const WhatToExpect = () => (
  <section id="expect" data-testid="expect-section" className="bg-[#FBF6EC] py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#6B21A8]">
            What to Expect on a Sunday Evening
          </h2>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
        {EXPECT_STEPS.map((step, i) => (
          <Reveal key={step.title} delay={i * 0.1}>
            <div
              data-testid={`expect-step-${i + 1}`}
              className={`h-full rounded-2xl border-2 border-transparent ${STEP_COLORS[i]} p-8 shadow-lg transition-all duration-300 hover:border-[#F2C14E] hover:shadow-2xl`}
            >
              <div className="flex items-center justify-between">
                <span className="font-serif text-4xl font-semibold text-[#F2C14E]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <step.icon className="h-7 w-7 text-[#F2C14E]" aria-hidden="true" />
              </div>
              <h3 className="mt-5 font-serif text-xl sm:text-2xl font-medium text-[#FBF6EC]">
                {step.title}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-[#F3EFE6]">{step.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ContactSummary = () => (
  <section data-testid="contact-summary-section" className="bg-gradient-to-br from-[#581C87] via-[#6B21A8] to-[#A61C4E] py-20 text-[#E9E4DA] sm:py-28">
    <div className="mx-auto max-w-5xl px-5 text-center sm:px-8">
      <Reveal>
        <p className="text-sm sm:text-base font-semibold uppercase tracking-[0.25em] text-[#F2C14E]">
          Find Us & Call Us
        </p>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#FBF6EC]">
          You Will Find Us at the Northfield Centre
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <address className="mt-8 font-serif text-xl not-italic leading-relaxed text-[#F3E8FA] sm:text-2xl">
          {CHURCH.addressLines.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </address>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <a
            href={CHURCH.phoneHref}
            data-testid="summary-call-button"
            className="inline-flex min-h-14 items-center gap-3 rounded-xl bg-[#C59B4B] px-8 text-lg font-semibold text-[#101826] shadow-md transition-all duration-200 hover:bg-[#d4ab5f] hover:shadow-lg active:scale-95"
          >
            <Phone className="h-5 w-5" aria-hidden="true" />
            Call {CHURCH.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="summary-directions-button"
            className="inline-flex min-h-14 items-center gap-3 rounded-xl border-2 border-[#F2C14E]/60 px-8 text-lg font-semibold text-[#FBF6EC] transition-all duration-200 hover:border-[#F2C14E] hover:text-[#F2C14E] active:scale-95"
          >
            <MapPin className="h-5 w-5" aria-hidden="true" />
            Directions & Parking
          </Link>
        </div>
        <p className="mt-8 flex items-center justify-center gap-2 text-base text-[#E9DFF2]">
          <Clock className="h-4 w-4 text-[#F2C14E]" aria-hidden="true" />
          Sunday Divine Service · Every Sunday at 6:30pm
        </p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <ServiceSpotlight />
      <About />
      <WhatToExpect />
      <ContactSummary />
    </>
  );
}
